import { useCallback, useEffect, useRef, useState } from 'react';

import { useQualityCheckService } from '../../contexts/QualityCheckServiceContext';
import { useQualityCheckState } from '../v2-hooks/useQualityCheckState';
import { ScanState } from '../../services/QualityCheckService';

const PROGRESS_DURATION_MS = 90_000;
const PROGRESS_STEP = 5;
const PROGRESS_INTERVAL_MS = PROGRESS_DURATION_MS / (100 / PROGRESS_STEP);

export interface QualityCheckModalViewModel {
  readonly isVisible: boolean;
  readonly scanState: ScanState;
  readonly progress: number;
  readonly error: string | undefined;
  readonly isCancelling: boolean;
  readonly canCancel: boolean;
  readonly cancel: () => void;
}

export function useQualityCheckModalViewModel(): QualityCheckModalViewModel {
  const qualityCheckService = useQualityCheckService();
  const { state: scanState, isScanConfirmed } = useQualityCheckState();
  const [showModal, setShowModal] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | undefined>(undefined);
  const [isCancelling, setIsCancelling] = useState(false);
  const progressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const prevScanStateRef = useRef(scanState);

  useEffect(() => {
    const prevState = prevScanStateRef.current;
    prevScanStateRef.current = scanState;

    if (scanState === 'scan_in_progress' && prevState !== 'scan_in_progress') {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
      setShowModal(true);
      setProgress(0);
      setError(undefined);
      setIsCancelling(false);

      progressIntervalRef.current = setInterval(() => {
        setProgress((prev) => {
          const next = prev + PROGRESS_STEP;
          if (next >= 100) {
            clearInterval(progressIntervalRef.current!);
            progressIntervalRef.current = null;
            return 100;
          }
          return next;
        });
      }, PROGRESS_INTERVAL_MS);
    }

    // Polling flipped the scan back to idle, so the cancellation (or a normal
    // completion) has landed. Close the modal and drop the cancelling flag.
    if (prevState === 'scan_in_progress' && scanState !== 'scan_in_progress') {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
      setShowModal(false);
      setIsCancelling(false);
    }

    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    };
  }, [scanState]);

  const cancel = useCallback(async () => {
    setError(undefined);
    setIsCancelling(true);
    try {
      // The modal stays open on success; polling drives the transition to idle,
      // and the effect above closes the modal when scanState leaves scan_in_progress.
      await qualityCheckService.cancel();
    } catch {
      setIsCancelling(false);
      setError('Failed to cancel quality check. Please try again.');
    }
  }, [qualityCheckService]);

  return {
    isVisible: showModal && scanState === 'scan_in_progress',
    scanState,
    progress,
    error,
    isCancelling,
    canCancel: isScanConfirmed,
    cancel,
  };
}
